import React, { useState } from "react";
import {
  Box,
  Typography,
  LinearProgress,
  Alert,
  CssBaseline,
  Button,
} from "@mui/material";
import axios from "axios";
import CreditsBanner from "../Components/CreditsNudge.js";
import {
  ContentBox,
  TitleText,
  SubtitleText,
  PageContainer,
} from "./Default.js";
import { Link } from "react-router-dom";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import { styled } from "@mui/material/styles";
import BulkEmailValidatorContent from "../Contents/BulkEmailValidatorContent.js";
import { message } from "../Constants/message.js";
import CommentSection from "../Components/CommentSection.js";

const UploadZone = styled(Box)(({ theme, isdragging }) => ({
  border: "2px dashed",
  borderColor: isdragging
    ? theme.palette.primary.main
    : theme.palette.grey[400],
  borderRadius: theme.shape.borderRadius,
  padding: theme.spacing(4),
  textAlign: "center",
  cursor: "pointer",
  backgroundColor: isdragging ? theme.palette.action.hover : "transparent",
  transition: "background-color 0.3s ease",
}));

const FileInput = styled("input")({
  display: "none",
});

const handleDownload = async () => {
  try {
    const response = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/misc/bulk-email-validator/sample_download`, { fileName: "email_validation_upload" }, {
      responseType: "blob", // Important for file download
    });
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "sample.csv"); // Optional: name the file
    document.body.appendChild(link);
    link.click();
    link.remove();
  } catch (error) {
    console.error("Download failed:", error);
  }
};

function BulkEmailValidator({ user, login, isLoggedIn }) {
  const [selectedFile, setSelectedFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [dragging, setDragging] = useState(false);

  const [credits, setCredits] = useState({
    remaining: 0,
    availed: 0,
    total: 0,
    loading: true,
  });

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    setDragging(false);
    setError("");
    setSuccess("");

    if (!file) return;

    if (file.type !== "text/csv" && !file.name.endsWith(".csv")) {
      setError("Only CSV files are supported.");
      return;
    }

    if (file.size > 1024 * 1024) {
      setError("File too large. Max size is 1MB.");
      return;
    }

    setSelectedFile(file);
  };

  const handleSubmit = async () => {
    setError("");
    setSuccess("");
    if (!isLoggedIn) {
      setError(message.loginPrompt);
      setTimeout(() => {
        login();
      }, 1500); 
      return;
    }

    if (!selectedFile) {
      setError("Please upload a CSV file containing emails.");
      return;
    }
    if (credits.remaining <= 0) {
      setError("Not enough credits to perform bulk email validation.");
      return;
    }

    const formData = new FormData();
    formData.append("file", selectedFile);

    setLoading(true);
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/misc/bulk-email-validator`,
        formData,
        {
          withCredentials: true,
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      setSuccess(response.data.successMessage);
      setSelectedFile(null);
      setCredits({
        remaining: response.data.remaining_credits || 0,
        availed: response.data.availed_credits || 0,
        total: response.data.total_credits || 0,
        loading: false,
      });
    } catch (err) {
      setError(
        err.response?.data?.errorMessage ||
          "Failed to process the file. Please check your input."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {isLoggedIn && (
        <CreditsBanner credits={credits} setCredits={setCredits} />
      )}
      <CssBaseline />
      <PageContainer  sx={{ paddingTop: 3 }}>
        <ContentBox>
          <TitleText variant="h3" align="center">
            Bulk Email Validator ➡️ CSV
          </TitleText>

          <SubtitleText variant="subtitle1" align="center">
            Upload a list of emails and get verified status in seconds
          </SubtitleText>

          <Box mb={3}>
            <FileInput
              id="email-file-upload"
              type="file"
              accept=".csv"
              onChange={handleFileChange}
            />
            <UploadZone
              isdragging={dragging ? 1 : 0}
              onDragOver={(e) => {
                e.preventDefault();
                setDragging(true);
              }}
              onDragLeave={() => setDragging(false)}
              onDrop={(e) => {
                e.preventDefault();
                setDragging(false);
                if (e.dataTransfer.files.length > 0) {
                  setSelectedFile(e.dataTransfer.files[0]);
                  setError("");
                  setSuccess("");
                }
              }}
              onClick={() =>
                document.getElementById("email-file-upload").click()
              }
            >
              <CloudUploadIcon
                sx={{ fontSize: 40, color: "primary.main", mb: 1 }}
              />
              <Typography variant="h6" gutterBottom>
                Drag & Drop your email CSV file here
              </Typography>
              <Typography variant="body2" color="textSecondary">
                or click to browse
              </Typography>
              {selectedFile && (
                <Typography variant="body2" sx={{ mt: 1 }} color="textPrimary">
                  Selected File: <strong>{selectedFile.name}</strong>
                </Typography>
              )}
            </UploadZone>
          </Box>

          {loading && <LinearProgress sx={{ mb: 2 }} />}

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {success}
            </Alert>
          )}

          <Box display="flex" justifyContent="center" gap={2}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleSubmit}
              disabled={loading}
            >
              Validate Emails
            </Button>
            <Button variant="outlined" color="primary" onClick={handleDownload}>
              📥 Download Sample CSV
            </Button>
          </Box>

          <Box mt={3}>
            <Typography variant="body2" color="text.secondary" align="center">
              💡 Uploading and successfully validating <b>1 CSV = 1 credit</b>. Have
              questions? Need help? <Link to="/contact">Contact us</Link>  📝
              <br />
              👉 In <b>Free</b> plan, you can validate up to 5 emails from a single CSV
              upload.  <br /> 👉 In <b>Starter</b> plan, you can validate up to 50
              emails from a single CSV upload. <br /> 👉 In <b>Growth</b> plan, you can
              validate up to 200 emails from a single CSV upload. <br /> 👉 In{" "}
              <b>Pro</b> plan, you can validate up to 5000 emails from a single CSV
              upload. <br /> 👉 In <b>Business</b> plan, you can validate up to 10000
              emails from a single CSV upload.
            </Typography>
          </Box>
        </ContentBox>
        <CommentSection toolId="misc/bulk-email-validator-tool" user={user} login={login} isLoggedIn={isLoggedIn}/>
        <BulkEmailValidatorContent />
      </PageContainer>
    </>
  );
}

export default BulkEmailValidator;